import { useNavigate } from 'react-router-dom'
import { useLanguage } from '@context/LanguageContext'
import { cx } from '@utils/cx'
import styles from './GameCard.module.css'

/**
 * GameCard - Presentational card component for game selection
 *
 * Displays a clickable card with game image and title.
 * Handles navigation to game route and image fallback.
 *
 * @param {Object} props
 * @param {Object} props.game - Game object from GAMES_INFO
 * @param {string} props.game.id - Game identifier
 * @param {string} props.game.route - Route segment for navigation
 * @param {string} props.game.image - Path to game image
 * @param {string} props.game.titleKey - i18n key for game title
 * @param {string} props.game.fallbackImage - Fallback image path
 * @param {string} [props.className] - Additional CSS classes
 */
export default function GameCard ({ game, index = 0, className = '' }) {
  const navigate = useNavigate()
  const { lang, tFunc } = useLanguage()

  const gameTitle = tFunc(game.titleKey)

  const handleClick = () => {
    navigate(`/${lang}/${game.route}`)
  }

  const handleImageError = (e) => {
    // Fallback to default image if game image fails to load
    if (e.target.src !== game.fallbackImage) {
      e.target.src = game.fallbackImage
    }
  }

  return (
    <button
      type='button'
      onClick={handleClick}
      className={cx(styles.gameCard, className)}
      aria-label={tFunc('home.access.goToGame').replace('{game}', gameTitle)}
      title={gameTitle}
    >
      <div className={styles.imageContainer}>
        <img
          src={game.image}
          alt=''
          className={styles.image}
          onError={handleImageError}
          loading={index < 2 ? 'eager' : 'lazy'}
          fetchPriority={index === 0 ? 'high' : undefined}
          decoding='async'
          aria-hidden='true'
        />
      </div>
      <h2 className={styles.title}>{gameTitle}</h2>
    </button>
  )
}
